import { type RefObject, useEffect } from "react";
import { type MatFallbackReason, trackMatVariant } from "../../../utils/tag-manager";
import { blockingReason, readLoadEnvironment, rememberSlowFrames } from "./load-conditions";
import { loadFigureModule } from "./load-figure";
import { afterLoadWhenIdle } from "./main-thread";
import { type MatFigureHandle, type Mat3dFigureModule } from "./mat3d-figure-types";
import { type Mat3dSlot, MAT3D_HOME_SLOT } from "./mat3d-slots";
import { type FigureCanvasPlacement, placeFigureCanvas } from "./slot-geometry";

/** Ueberblendung SVG ↔ 3D. */
const FADE = "opacity 240ms ease";

/** Kontext-Attribute: wie three.js' Vorgaben, ohne Stencil, sparsame GPU. */
const GL_ATTRIBUTES: WebGLContextAttributes = {
    alpha: true,
    antialias: true,
    depth: true,
    stencil: false,
    premultipliedAlpha: true,
    powerPreference: "low-power",
};

const matchesQuery = (query: string): boolean => {
    try {
        return window.matchMedia?.(query).matches ?? false;
    } catch {
        return false;
    }
};

/**
 * Die 3D-Insel in einem Mat-Slot: auf der Startseite (Plan
 * `2026-10-02-mat-3d-website`, Welle 2, Vorgabe) und im Hero der
 * KI-Technologie-Seite (`MAT3D_KI_SLOT`, Plan `2026-10-10-mat-3d-ki-technologie`).
 * Was sich je Slot unterscheidet — Sichtbarkeit, Eventpraefix, Traeger der
 * Leinwand, Ueberstand —, steht in `mat3d-slots.ts`. Das SSG-HTML und der erste
 * Paint bleiben die SVG-Figur; die Insel aendert am Markup nichts und legt im
 * Browser eine Leinwand ueber sie.
 *
 * Sie laedt den 3D-Chunk nur, wenn der Slot es erlaubt (KI-Seite: ab `lg`),
 * ohne reduzierte Bewegung, ohne Datensparen, mit WebGL2 und erst, wenn der Slot
 * im Viewport ist — und dann erst in einer Leerlaufphase nach `load`
 * (spaetestens nach `MAT3D_IDLE_TIMEOUT_MS`, Welle 3). Jeder Rueckfall auf die
 * SVG meldet `mat_fallback` (KI-Seite: `ki_mat_fallback`), auch ein
 * gescheiterter Chunk-Load (`chunk_load`).
 *
 * Die Leinwand faengt keine Klicks (`pointer-events: none`): Klicks treffen
 * weiter die (dann unsichtbare) SVG-Figur. Auf der Startseite liegt die
 * Leinwand IM Link des Teasers, die SVG springt und navigiert — `cta_click`
 * bleibt unveraendert, und unter `md` sperrt der Halter wie bisher alle Klicks.
 */
export const useMat3dIsland = (slotRef: RefObject<HTMLElement>, config: Mat3dSlot = MAT3D_HOME_SLOT): void => {
    useEffect(() => {
        const slot = slotRef.current;
        if (!slot) {
            return;
        }
        if (config.visibleFrom && !matchesQuery(config.visibleFrom)) {
            return;
        }
        if (config.reportsExposure) {
            trackMatVariant({ event: "mat_exposure", variant: "3d" }, config.context);
        }
        const report = (reason: MatFallbackReason, healCount = 0): void => {
            trackMatVariant({ event: "mat_fallback", variant: "3d", reason, healCount }, config.context);
        };
        const blocked = blockingReason(readLoadEnvironment());
        if (blocked) {
            report(blocked);
            return;
        }
        const svg = slot.querySelector<SVGSVGElement>("[data-testid=mat-figure]");
        const anchor = svg ? config.canvasHost(slot, svg) : null;
        if (!svg || !anchor) {
            return;
        }
        const measure = (): FigureCanvasPlacement =>
            placeFigureCanvas({
                anchorRect: anchor.getBoundingClientRect(),
                anchorLayoutWidth: anchor.offsetWidth,
                svgRect: svg.getBoundingClientRect(),
                bleed: config.bleed,
            });

        let figure: MatFigureHandle | null = null;
        let canvas: HTMLCanvasElement | null = null;
        let started = false;
        let inView = false;
        let gone = false;
        const cleanups: Array<() => void> = [];

        const showSvg = (): void => {
            svg.style.transition = FADE;
            svg.style.opacity = "1";
            if (canvas) {
                canvas.style.opacity = "0";
            }
        };
        const show3d = (): void => {
            svg.style.transition = FADE;
            svg.style.opacity = "0";
            if (canvas) {
                canvas.style.opacity = "1";
            }
        };
        const place = (): void => {
            if (!canvas) {
                return;
            }
            const placement = measure();
            Object.assign(canvas.style, {
                left: `${placement.left}px`,
                top: `${placement.top}px`,
                width: `${placement.width}px`,
                height: `${placement.height}px`,
            });
            figure?.resize(placement.frame);
        };
        const removeCanvas = (): void => {
            canvas?.remove();
            canvas = null;
        };
        const stop = (): void => {
            figure?.dispose();
            figure = null;
            removeCanvas();
        };
        const syncRunning = (): void => {
            if (!figure) {
                return;
            }
            if (inView && document.visibilityState !== "hidden") {
                figure.resume();
            } else {
                figure.pause();
            }
        };

        const fallBack = (reason: MatFallbackReason, healCount = 0): void => {
            showSvg();
            report(reason, healCount);
            if (reason === "slow_frames") {
                rememberSlowFrames();
            }
            if (reason !== "context_lost") {
                stop();
            }
        };

        const start = async (): Promise<void> => {
            if (gone) {
                return;
            }
            canvas = document.createElement("canvas");
            canvas.setAttribute("aria-hidden", "true");
            canvas.dataset.mat3d = "figure";
            Object.assign(canvas.style, {
                position: "absolute",
                pointerEvents: "none",
                opacity: "0",
                transition: FADE,
            });
            const gl = canvas.getContext("webgl2", GL_ATTRIBUTES);
            if (!gl) {
                canvas = null;
                report("no_webgl");
                return;
            }
            anchor.appendChild(canvas);
            const placement = measure();
            place();

            let module: Mat3dFigureModule;
            try {
                module = await loadFigureModule();
            } catch (err) {
                console.warn("[mat] 3D chunk failed to load, keeping the SVG", err);
                removeCanvas();
                showSvg();
                report("chunk_load");
                return;
            }
            if (gone || !canvas) {
                return;
            }
            let handle: MatFigureHandle;
            try {
                handle = await module.startMatFigure({
                    canvas,
                    gl,
                    frame: placement.frame,
                    follow: window.matchMedia?.("(hover: hover)").matches ?? false,
                    onReady: show3d,
                    onFail: fallBack,
                });
            } catch (err) {
                console.warn("[mat] 3D init failed, keeping the SVG", err);
                removeCanvas();
                showSvg();
                report("shader");
                return;
            }
            // Waehrend des Aufbaus verschwunden oder schon gescheitert (Shader-Fehler im Aufbau)
            if (gone || !canvas) {
                handle.dispose();
                return;
            }
            figure = handle;
            syncRunning();
        };

        let cancelIdle: (() => void) | null = null;
        cleanups.push(() => cancelIdle?.());

        const observer = new IntersectionObserver(entries => {
            inView = entries.some(entry => entry.isIntersecting);
            if (inView && !started) {
                started = true;
                cancelIdle = afterLoadWhenIdle(() => {
                    void start();
                });
            }
            syncRunning();
        });
        observer.observe(anchor);
        cleanups.push(() => observer.disconnect());

        const onVisibility = (): void => syncRunning();
        document.addEventListener("visibilitychange", onVisibility);
        cleanups.push(() => document.removeEventListener("visibilitychange", onVisibility));

        const onResize = (): void => place();
        window.addEventListener("resize", onResize);
        cleanups.push(() => window.removeEventListener("resize", onResize));

        const onClick = (): void => figure?.jump();
        const onEnter = (): void => figure?.attention();
        svg.addEventListener("click", onClick);
        svg.addEventListener("pointerenter", onEnter);
        cleanups.push(() => {
            svg.removeEventListener("click", onClick);
            svg.removeEventListener("pointerenter", onEnter);
        });

        const onPointer = (event: PointerEvent): void => figure?.pointer(event.clientX, event.clientY);
        window.addEventListener("pointermove", onPointer, { passive: true });
        cleanups.push(() => window.removeEventListener("pointermove", onPointer));

        return () => {
            gone = true;
            cleanups.forEach(cleanup => cleanup());
            stop();
            svg.style.removeProperty("opacity");
            svg.style.removeProperty("transition");
        };
    }, [slotRef, config]);
};
